import { useSyncExternalStore } from "react";

/** Shared localStorage key used independently on each app origin. */
const AUTH_STORAGE_KEY = "nazare-session";

/**
 * Shape of the auth session persisted by each app's auth bootstrap.
 *
 * This is intentionally loose (`user: unknown`) — this package shouldn't
 * depend on nazare-frontend's app-specific `User` type, and consumers
 * should narrow/validate `user` themselves if they need its shape.
 */
export type StoredAuth = {
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  user?: unknown;
};

// Module-level cache so repeated reads of an unchanged localStorage value
// return the *same* object reference. This isn't just an optimization: it's
// required for `useStoredAuth`'s use of `useSyncExternalStore` below, whose
// snapshot must be referentially stable when nothing has changed, or React
// treats every render as a fresh change and re-renders in a loop.
let cachedRaw: string | null = null;
let cachedValue: StoredAuth | null = null;

function readStoredAuth(): StoredAuth | null {
  if (typeof window === "undefined") return null;

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
  } catch {
    // localStorage can throw (e.g. disabled/blocked storage access) — treat
    // as "not logged in" rather than surfacing an error.
    raw = null;
  }

  if (raw === cachedRaw) {
    return cachedValue;
  }
  cachedRaw = raw;

  if (!raw) {
    cachedValue = null;
    return cachedValue;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    cachedValue =
      parsed && typeof parsed === "object" ? (parsed as StoredAuth) : null;
  } catch {
    cachedValue = null;
  }

  return cachedValue;
}

/**
 * Reads the auth session persisted on the current app origin at
 * `localStorage["nazare-session"]`.
 *
 * Defensive by design — returns `null` if the key is missing, empty, or
 * fails to parse as JSON, rather than throwing.
 */
export function getStoredAuth(): StoredAuth | null {
  return readStoredAuth();
}

function subscribe(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  // Only fires when *another* tab/window writes localStorage — a same-tab
  // write doesn't dispatch "storage". That's fine here: this app never
  // writes from another tab are the only changes this read-only helper needs
  // to subscribe to; an app bootstrap writes before its consumers render.
  window.addEventListener("storage", onStoreChange);
  return () => window.removeEventListener("storage", onStoreChange);
}

function getServerSnapshot(): StoredAuth | null {
  return null;
}

/**
 * React hook version of {@link getStoredAuth} — returns the same shape, and
 * re-reads it when another tab writes (or clears) the "nazare-session" key
 * so a login/logout in the main app is reflected without a manual refresh.
 */
export function useStoredAuth(): StoredAuth | null {
  return useSyncExternalStore(subscribe, readStoredAuth, getServerSnapshot);
}

/**
 * Whether a {@link StoredAuth} session's access token has (or should be
 * treated as having) already expired.
 *
 * The current auth singleton persists `expiresAt` as Unix epoch milliseconds.
 * A session migrated from the legacy store can retain epoch seconds until its
 * first token refresh, so both representations are accepted here.
 */
export function isStoredAuthExpired(
  auth: StoredAuth | null | undefined,
): boolean {
  // No expiry info at all: nothing to compare against, so don't assume
  // expired (callers still gate on token *presence* separately).
  if (!auth?.expiresAt) return false;
  const expiresAt =
    auth.expiresAt < 1e12 ? auth.expiresAt * 1000 : auth.expiresAt;
  return expiresAt <= Date.now();
}

/** Whether a {@link StoredAuth} has enough to attempt at least one request
 * (a still-fresh access token, or a refresh token to trade for one) —
 * callers gate `useQuery({ enabled })` on this so a page with no session at
 * all doesn't fire a request that's guaranteed to 401. */
export function hasStoredSession(auth: StoredAuth | null | undefined): boolean {
  return Boolean(auth?.accessToken || auth?.refreshToken);
}

function authHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * Thrown when a BFF request can't be completed because the session itself
 * is gone (no refresh token, or refreshing it failed) — as opposed to a
 * generic network/server error. Lets callers show an honest "sign in again"
 * state instead of a bare "couldn't load" error.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired");
    this.name = "SessionExpiredError";
  }
}

type RefreshedAuth = {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
};

// Module-scoped, session-only cache of a silently-refreshed access token.
// Not persisted to localStorage — this package only *reads* nazare-frontend's
// auth session (see `useStoredAuth`'s doc comment above), it doesn't own
// writing it back. So a refreshed token here lives only for this tab's
// lifetime; a reload re-reads the (still-stale) stored token, sees it's
// expired again, and silently refreshes again. Wasteful across reloads, but
// correct, and no worse than requiring a fresh sign-in.
let refreshedAuth: RefreshedAuth | null = null;

// De-dupes concurrent refresh attempts (e.g. two different queries both
// hitting an expired token at once) into a single in-flight network request
// rather than each firing their own.
let refreshInFlight: Promise<RefreshedAuth | null> | null = null;

// Mirrors nazare-frontend's real `RefreshToken` GraphQL mutation (see
// `app/web/app/screens/login/login.gql.ts`'s generated `RefreshTokenDocument`
// and `app/web/app/screens/login/refreshToken.gql.ts`'s `refreshTokenCall()`)
// as a plain string rather than pulling in the `graphql` package just to
// print an AST this package doesn't otherwise need.
const REFRESH_TOKEN_MUTATION = `
  mutation RefreshToken($token: String!) {
    refreshToken(refreshRequestInput: { refreshToken: $token }) {
      accessToken
      refreshToken
      expiresAt
      user
    }
  }
`;

async function performRefresh(
  baseUrl: string,
  refreshToken: string,
): Promise<RefreshedAuth | null> {
  try {
    const res = await fetch(`${baseUrl}/graphql`, {
      method: "POST",
      headers: authHeaders(refreshToken),
      body: JSON.stringify({
        query: REFRESH_TOKEN_MUTATION,
        variables: { token: refreshToken },
      }),
    });
    if (!res.ok) return null;
    const json: {
      data?: { refreshToken?: Partial<RefreshedAuth> | null };
    } = await res.json();
    const result = json.data?.refreshToken;
    if (
      !result?.accessToken ||
      !result.refreshToken ||
      typeof result.expiresAt !== "number"
    ) {
      return null;
    }
    refreshedAuth = result as RefreshedAuth;
    return refreshedAuth;
  } catch {
    return null;
  }
}

/**
 * Replicates nazare-frontend's real `refreshTokenCall()` — a plain POST to
 * the same GraphQL endpoint using only `StoredAuth.refreshToken`, with no
 * dependency on Apollo or any in-memory OAuth client state, so it's fully
 * reproducible here from just what's already in `localStorage`. De-duped:
 * concurrent callers share one in-flight request.
 */
function refreshAccessToken(
  baseUrl: string,
  refreshToken: string,
): Promise<RefreshedAuth | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh(baseUrl, refreshToken).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Best refresh token available: a just-refreshed one, else the stored one. */
function currentRefreshToken(auth: StoredAuth | null): string | undefined {
  return refreshedAuth?.refreshToken ?? auth?.refreshToken;
}

/** Attempts one silent refresh; returns the new access token, or null. */
async function forceRefresh(
  auth: StoredAuth | null,
  baseUrl: string,
): Promise<string | null> {
  const refreshToken = currentRefreshToken(auth);
  if (!refreshToken) return null;
  const refreshed = await refreshAccessToken(baseUrl, refreshToken);
  return refreshed?.accessToken ?? null;
}

/**
 * Resolves the access token to send with a request: a still-fresh
 * session-refreshed token if we have one, else the stored token if it's not
 * (yet) locally expired, else one silent refresh attempt. Throws
 * `SessionExpiredError` if none of those produce a usable token.
 */
async function resolveAccessToken(
  auth: StoredAuth | null,
  baseUrl: string,
): Promise<string> {
  if (refreshedAuth && !isStoredAuthExpired(refreshedAuth)) {
    return refreshedAuth.accessToken;
  }
  if (auth?.accessToken && !isStoredAuthExpired(auth)) {
    return auth.accessToken;
  }
  const token = await forceRefresh(auth, baseUrl);
  if (token) return token;
  throw new SessionExpiredError();
}

/**
 * Auth-aware fetch shared by every BFF consumer in this package (the
 * notification feed, QA Automation, and any future one) — a single place
 * for the token-resolve/refresh/retry-once-on-401 dance, so it isn't
 * reimplemented per-feature.
 *
 * Resolves a token via {@link resolveAccessToken} (which already refreshes
 * proactively if the stored token looks locally expired), then makes the
 * request. If the server *still* rejects it with a 401 — a real failure
 * mode confirmed live in dev: `StoredAuth.expiresAt` can be stale/out of
 * sync with the access JWT's own `exp` claim, so a token can look
 * locally-valid and still be genuinely expired server-side — attempts
 * exactly one more silent refresh-and-retry before giving up.
 */
export async function authedFetch(
  baseUrl: string,
  auth: StoredAuth | null,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = await resolveAccessToken(auth, baseUrl);
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: { ...authHeaders(token), ...init.headers },
  });
  if (res.status !== 401) return res;

  const refreshedToken = await forceRefresh(auth, baseUrl);
  if (!refreshedToken) throw new SessionExpiredError();

  return fetch(`${baseUrl}${path}`, {
    ...init,
    headers: { ...authHeaders(refreshedToken), ...init.headers },
  });
}
